import { Button, Tooltip } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import * as React from 'react';
import { UseQueryResult } from '@tanstack/react-query';

import {
  EmptyReturnAvailableResponse,
  Filter,
  SmartScreenshotResponse,
} from 'services/clients/perDiem';
import { PerDiemDisputeTable } from 'components/PerDiemDisputeTable/PerDiemDisputeTable';
import {
  DownloadModal,
  ErrorModal,
  UpgradeModal,
} from 'pages/PerDiemArchive/views/smart-per-diem/download-modal';
import { Container } from 'types/Watchlist';
import { getPortCollection } from 'utils/container-transforms';
import { Permission, useHasPermission } from 'utils/permissions';

interface Props {
  container: Container;
  queryResult: UseQueryResult<EmptyReturnAvailableResponse>;
  setPageSize: (pageSize: number) => void;
  setPage: (page: number) => void;
  pageSize: number;
  page: number;
}

interface DownloadModalState {
  modal: 'download';
}

interface UpgradeModalState {
  modal: 'upgrade';
}

interface ErrorModalState {
  modal: 'error';
  title: string;
  message: string;
}

type ModalState = DownloadModalState | UpgradeModalState | ErrorModalState;

export const DisputeTable: React.FC<Props> = (props) => {
  const { queryResult, setPage, setPageSize, page, pageSize, container } = props;

  const hasDownloadPermission = useHasPermission([Permission.PerDiemSmartPerDiemDownload]);

  const [modalState, setModalState] = React.useState<ModalState | null>(null);

  const emailReport = React.useCallback(() => {
    setModalState({ modal: 'download' });
  }, []);

  const handlePerDiemModal = React.useCallback((code: SmartScreenshotResponse['code']) => {
    switch (code) {
      case 'no_credits':
        setModalState({ modal: 'upgrade' });
        break;
      case 'error':
      case 'no_screenshot':
        setModalState({
          modal: 'error',
          title: 'There was an error retrieving your screenshot',
          message:
            'We apologize for the inconvenience. Please try to download the screenshot again.',
        });
        break;
      default:
        break;
    }
  }, []);

  const filter: Filter | undefined = React.useMemo(() => {
    const shippingLine = container.shipment?.shippingLine.key;
    const { containerTypeKey } = container;

    const startDate = queryResult.data?.startDate;
    const endDate = queryResult.data?.endDate;
    const portCollection = getPortCollection(container);

    if (!shippingLine || !containerTypeKey || !startDate || !endDate || !portCollection) {
      return undefined;
    }

    const f: Filter = {
      shippingLine,
      containerType: containerTypeKey,
      startDate,
      endDate,
      portCollection,
    };

    return f;
  }, [container, queryResult.data]);

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between px-4">
          <p className="text-gray-900 font-medium">Disputable Days</p>
          {hasDownloadPermission ? (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<MailOutlineIcon />}
              onClick={emailReport}
              disabled={!filter}
            >
              Email report
            </Button>
          ) : (
            <Tooltip
              title="Upgrade to access this feature"
              classes={{ tooltip: `bg-gray-600 text-white p-3` }}
            >
              <div>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<MailOutlineIcon />}
                  disabled
                >
                  Email report
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
        <PerDiemDisputeTable
          onModal={handlePerDiemModal}
          loading={queryResult.isPreviousData || queryResult.isLoading}
          pageSize={pageSize}
          page={page}
          setPageSize={setPageSize}
          setPage={setPage}
          data={queryResult.data}
          variant="minimal"
        />
      </div>
      {hasDownloadPermission && filter && (
        <DownloadModal
          open={modalState?.modal === 'download'}
          filter={filter}
          onClose={() => setModalState(null)}
          feature="containerSideDrawer"
          selectedContainer={container.containerNumber}
        />
      )}
      <UpgradeModal open={modalState?.modal === 'upgrade'} onClose={() => setModalState(null)} />
      <ErrorModal
        open={modalState?.modal === 'error'}
        // eslint-disable-next-line
        // @ts-ignore
        title={modalState?.title}
        // eslint-disable-next-line
        // @ts-ignore
        message={modalState?.message}
        onClose={() => setModalState(null)}
      />
    </>
  );
};
DisputeTable.displayName = 'Shipments.DetailDrawer.Sections.PerDiem.DisputeTable';
